import { useTranslations } from '@vocab/react';
import { Button, IconAI, Stack, Text } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { useSignedInDashboardContext } from '../../SignedInDashboardContextProvider';
import { CareerFeedAiAssistantInitialState } from '../CareerFeedAiAssistantInitialState/CareerFeedAiAssistantInitialState';
import { CareerFeedAssistantQuestions } from '../CareerFeedAssistantQuestions/CareerFeedAssistantQuestions';
import {
  useGetRefreshRecommendationsWithCandidateChatContext,
  useGetResetCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import translations from './.vocab';
import { CareerFeedAIAssistantContentFrame } from './CareerFeedAIAssistantContentFrame';

const ConversationFinished = () => {
  const { t } = useTranslations(translations);

  const {
    lastCompletedAssistantResponse,
    tracking: {
      trackRecommendedJobsRefreshed,
      trackGenAIChatEnded,
      trackGenAIChatInputPressed,
    },
  } = useCareerFeedAIAssistant();
  const {
    recommendationsResult: { isRefreshLoading },
  } = useSignedInDashboardContext();

  const message = lastCompletedAssistantResponse?.text ?? '';

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  const onRefresh = useCallback(async () => {
    trackGenAIChatInputPressed(message, []);
    const result = await refreshRecommendations();

    if (result.success) {
      trackGenAIChatEnded();
      trackRecommendedJobsRefreshed({ jobsCount: result.jobsCount });
    } else {
      trackGenAIChatEnded(Boolean(result.error));
      trackRecommendedJobsRefreshed({ jobsCount: -1 });
    }
  }, [
    trackGenAIChatInputPressed,
    message,
    refreshRecommendations,
    trackGenAIChatEnded,
    trackRecommendedJobsRefreshed,
  ]);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={message}
      ctaSection={
        <Stack space="small">
          <Button
            tone="brandAccent"
            icon={<IconAI />}
            onClick={onRefresh}
            loading={isRefreshLoading}
          >
            {t('Explore recommendations')}
          </Button>
          <Text size="xsmall" tone="secondary">
            {t('These AI-powered job suggestions....')}
          </Text>
        </Stack>
      }
    />
  );
};

const RecommendationRefinedConfirmation = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const {
    tracking: { trackGenAIChatResetPressed },
  } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  const onReset = useCallback(() => {
    trackGenAIChatResetPressed('chat widget');
    resetPreferences();
  }, [resetPreferences, trackGenAIChatResetPressed]);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={t(
        "We've updated your recommendations based on your answers. Take a look at the personalised job listings.",
      )}
      ctaSection={<Button onClick={onReset}>{t('Reset')}</Button>}
    />
  );
};

export const CareerFeedAIAssistantContent = () => {
  const {
    sequence,
    progress,
    tracking: {
      trackGenAIChatOutputDisplayed,
      trackGenAIChatStarted,
      trackGenAIChatCtaDisplayed,
    },
  } = useCareerFeedAIAssistant();

  useEffect(() => {
    if (progress === 'initial') {
      trackGenAIChatCtaDisplayed();
    } else if (
      progress !== 'recsRefined' &&
      progress !== 'recsRefined-withPersistedData'
    ) {
      trackGenAIChatOutputDisplayed();
    }

    if (sequence === 1) {
      trackGenAIChatStarted();
    }
  }, [
    progress,
    sequence,
    trackGenAIChatCtaDisplayed,
    trackGenAIChatOutputDisplayed,
    trackGenAIChatStarted,
  ]);

  switch (progress) {
    case 'initial':
      return <CareerFeedAiAssistantInitialState />;
    case 'recsRefined':
    case 'recsRefined-withPersistedData':
      return <RecommendationRefinedConfirmation />;
    case 'reviewRefinement':
      return <ConversationFinished />;
    case 'gatheringRefinement':
    case 'collectGeneralRequirementsAtLast':
      return <CareerFeedAssistantQuestions />;
  }
};
